import { createAPIClient } from '@/utils/network-utils/createAPIClient';
import { EnvironmentService } from '@/services/isomorphic/EnvironmentService';
import Service from '@/services/Service';
import DOMSOrderService from '@/services/serverless/integrations/DOMS/DOMSOrderService';
import type { IOrderCancelStatus } from '@/services/serverless/integrations/DOMS/DOMSOrderService/types';
import CSRFTokenService from '../CSRFTokenService';

/**
 * Isomorphic service for handling order cancellation which interacts
 * with the {@link DOMSOrderService} if the request is handled in the server
 * or performs the respective API call(s).
 * */
class CancelOrderService extends Service {
  private client = createAPIClient({ baseURL: '/api/order' });

  /**
   * Place on order for cancellation.
   * @param orderID - The order ID.
   * @returns - The {@link IOrderCancelStatus}.
   */
  public async place(orderID: string): Promise<IOrderCancelStatus> {
    if ((typeof window === "undefined")) {
      return DOMSOrderService.placeCancelOrder(orderID);
    }
    const response = await this.client.post<IOrderCancelStatus>(
      `/${orderID}/cancel/place`,
      // The server will be in charge of sending the right payload
      // so we use undefined in here.
      undefined,
      { headers: CSRFTokenService.getHeaders() }
    );
    return response.data;
  }

  /**
   * Retrieve the cancellation status of the order.
   * @param orderID - The order ID.
   * @returns - The @{link IOrderCancelStatus status} of the order.
   */
  public async getStatus(orderID: string): Promise<IOrderCancelStatus> {
    if ((typeof window === "undefined")) {
      return DOMSOrderService.getCancelStatus(orderID);
    }

    const response = await this.client.get<IOrderCancelStatus>(
      `/${orderID}/cancel-status`,
      { headers: CSRFTokenService.getHeaders() }
    );
    return response.data;
  }
}

export default new CancelOrderService();
